import * as React from "react";
import { MEDIA_VARIANTS } from "@/content/media-variants";
import {
  EXTRA_MEDIA_VARIANTS,
  type MediaSet,
} from "@/content/media-variants-extra";

type Props = {
  variantKey: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  imgClassName?: string;
  sizes?: string;
  /** Above-the-fold image: eager + high fetch priority, never lazy. */
  priority?: boolean;
  aspect?: string;
  /** Optional placeholder variant key for skeleton or blur (e.g. low-res thumbnail). */
  placeholderKey?: string;
};

/**
 * Resolve a variant key to a normalized AVIF/WebP source set.
 * Generated entries expose fixed 640w/1280w pairs; hand-registered entries
 * (screenshots, photography) carry their own width ladder + intrinsic size.
 */
function resolve(key: string): MediaSet | undefined {
  const extra = EXTRA_MEDIA_VARIANTS[key];
  if (extra) return extra;
  const v = MEDIA_VARIANTS[key];
  if (!v) return undefined;
  return {
    avif: [
      { url: v.avif640, w: 640 },
      { url: v.avif1280, w: 1280 },
    ],
    webp: [
      { url: v.webp640, w: 640 },
      { url: v.webp1280, w: 1280 },
    ],
    width: 1280,
    height: 720,
  };
}

const srcset = (sources: MediaSet["avif"]) =>
  sources.map((s) => `${s.url} ${s.w}w`).join(", ");

function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={`animate-pulse rounded-2xl bg-surface-2 ${className ?? ""}`}
    />
  );
}

/**
 * Responsive <picture> that serves AVIF → WebP with a width-based srcset.
 *
 * Rules enforced here so call sites can't regress them:
 *  - `priority` images are eager + fetchpriority="high" (LCP candidates)
 *  - everything else is lazy + decoding="async"
 *  - intrinsic width/height are always emitted so the browser reserves
 *    layout space before the bytes land (no CLS)
 */
export function Picture({
  variantKey,
  alt,
  width,
  height,
  className,
  imgClassName,
  sizes = "(min-width: 1024px) 800px, 100vw",
  priority = false,
  aspect,
  placeholderKey,
}: Props) {
  const [loaded, setLoaded] = React.useState(false);
  
  const v = resolve(variantKey);


  if (!v) {
    // Attempt to resolve as local asset if variant lookup fails
    if (variantKey && (variantKey.startsWith('/') || variantKey.startsWith('http'))) {
      return (
        <div className={`relative overflow-hidden ${className ?? ""}`} style={aspect ? { aspectRatio: aspect } : {}}>
          <img src={variantKey} alt={alt} className={`${imgClassName ?? "w-full h-auto"}`} />
        </div>
      );
    }
    console.warn(`[Picture] Missing variant metadata for key: "${variantKey}"`);
    return null;
  }

  const fallback = v.webp && v.webp.length > 0 ? v.webp[v.webp.length - 1] : null;
  
  if (!fallback) {
    console.warn(`[Picture] No fallback WebP source for key: "${variantKey}" - rendering nothing.`);
    return null;
  }
  if (!fallback) {
    console.error(`[Picture] No fallback WebP found for variant: "${variantKey}"`);
    return null;
  }
  const w = width ?? v.width;
  const h = height ?? Math.round((v.height / v.width) * w);

  const containerStyle = aspect
    ? { aspectRatio: aspect }
    : { aspectRatio: `${w}/${h}` };

  return (
    <div
      className={`relative overflow-hidden ${className ?? ""}`}
      style={containerStyle}
    >
      {/* Skeleton shown until main image loads */}
      {!loaded && <Skeleton className="absolute inset-0 h-full w-full" />}

      <picture>
        <source type="image/avif" srcSet={srcset(v.avif)} sizes={sizes} />
        <source type="image/webp" srcSet={srcset(v.webp)} sizes={sizes} />
        <img
          src={fallback.url}
          srcSet={srcset(v.webp)}
          sizes={sizes}
          alt=""
          width={w}
          height={h}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          fetchPriority={priority ? "high" : "auto"}
          onLoad={() => setLoaded(true)}
          onError={() => {
            console.error(`[Picture] Failed to load image: ${fallback.url}`);
            setLoaded(true); // Stop showing skeleton if load fails
          }}
          className={`${imgClassName ?? "w-full h-auto"} transition-opacity duration-500 will-change-opacity opacity-100`}
        />
      </picture>
    </div>
  );
}
